import * as THREE from "three";

export class ParticlesSystem {
    
    /**
    * @param {object} params
    * - scene
    * - emitter : Plane
    * - particle : Object3D
    * - useLocal : Boolean
    * - distribution : string 
    * - particleScale : Vector3
    * - particleScaleVariation : Vector3
    * - followNormal : Boolean
    * - normalVariation : Vector3
    * - particleTransSpeed : Number or Vector3
    * - particleTransVariation : Number or Vector3
    * - particleRotSpeed : Vector3
    * - particleRotVariation : Vector3
    * - particleScaleSpeed : Vector3
    * - particleScaleSpeedVariation : Vector3
    * - particleMaxDelay : Number
    * - particleLife : Number
    * - particleLifeVariation : Number
    * - count : Number
    * - oneShot : Boolean
    */

    constructor(params) {
        this.scene = params.scene
        this.name = params.name || "ps"+Math.floor(Math.random()*1000),
        this.emitter = params.emitter || this.createEmitter()  
        this.particle = params.particle || this.createBaseParticle() 
        this.distribution = params.distribution || "volume"
        this.particleScale = params.particleScale || new THREE.Vector3(1,1,1)
        this.particleScaleVariation = params.particleScaleVariation || new THREE.Vector3(0,0,0)
        this.useLocal = params.useLocal || false;

        this.followNormal = params.followNormal || false
        if (this.followNormal){
            this.normalVariation = params.normalVariation || new THREE.Vector3(0,0,0)
            this.particleTransSpeed = params.particleTransSpeed || .1 
            this.particleTransVariation = params.particleTransVariation || .02 
        }else{
            this.particleTransSpeed = params.particleTransSpeed || new THREE.Vector3(.1,.1,.1) 
            this.particleTransVariation = params.particleTransVariation || new THREE.Vector3(.02,.02,.02) 
        }

        this.particleRotSpeed = params.particleRotSpeed || new THREE.Vector3(.1,.1,.1) 
        this.particleRotVariation = params.particleRotVariation || new THREE.Vector3(.1,.1,.1) 
        
        this.particleScaleSpeed = params.particleScaleSpeed || new THREE.Vector3(1,1,1) 
        this.particleScaleSpeedVariation = params.particleScaleSpeedVariation || new THREE.Vector3(0,0,0) 
        
        this.particleMaxDelay = params.particleMaxDelay || 2
        this.particleLife = params.particleLife || 5
        this.particleLifeVariation = params.particleLifeVariation || 0
        this.count = params.count || 2000
        this.oneShot = params.oneShot || false;

        this.positions = []
        this.rotations = []
        this.scales = []
        this.times = []
        this.lifes = []
        this.delays = []
        this.transSpeeds = []
        this.rotSpeeds = []
        this.scaleSpeeds = []
        this.isAlive = []
        this.isActive = []

        this.emitter.updateMatrixWorld();
        if (!this.emitter.geometry.boundingBox) this.emitter.geometry.computeBoundingBox()
        this.bbox = this.emitter.geometry.boundingBox;
        this.normalDirection = new THREE.Vector3(0,1,0);
        
        if (this.useLocal) {
            if (!params.emitterVisible) {
                this.emitter.material.opacity = 0;
            }
            this.startPosition = this.emitter.position.clone();
        }else{
            this.emitter.visible = params.emitterVisible || false;
            this.startPosition = this.emitter.localToWorld(new THREE.Vector3());
            this.emitter.getWorldDirection(this.normalDirection)
        }
        

        this.initArrays()

        this.dummy = new THREE.Object3D();

        this.aliveParticles = this.count;

    }

    initArrays(){
        for (let i = 0; i < this.count; i++) {
            this.positions[i] = new THREE.Vector3()
            this.rotations[i] = new THREE.Vector3()
            this.scales[i] = new THREE.Vector3()
            this.times[i] = 0
            this.lifes[i] = 0
            this.delays[i] = 0
            this.transSpeeds[i] = (this.followNormal)? 0 : new THREE.Vector3()
            this.rotSpeeds[i] = new THREE.Vector3()
            this.scaleSpeeds[i] = new THREE.Vector3()
            this.isActive[i] = 0
            this.isAlive[i] = 1
        }
    }

    createEmitter(){
        const geom = new THREE.BoxGeometry(1,1,1)
        const mat = new THREE.MeshBasicMaterial()
        const emitter = new THREE.Mesh(geom, mat)
        emitter.geometry.computeBoundingBox();
        this.scene.add(emitter)
        return emitter
    }

    createBaseParticle(){
        const geom = new THREE.PlaneGeometry(.1,.1)
        const mat = new THREE.MeshBasicMaterial({
            color: 0x777777, 
            side:THREE.DoubleSide, 
            transparent:true,
            opacity: .5,
        })
        const particle = new THREE.Mesh(geom, mat)
        return particle
    }

    createParticles() {
        const geometryParticle = this.particle.geometry.clone();
        const materialParticle = this.particle.material.clone();
        this.particles = new THREE.InstancedMesh(geometryParticle, materialParticle, this.count);

        for (let i = 0; i < this.count; i++) {
            this.isActive[i] = 0
            this.isAlive[i] = 1
            this.resetParticle(i)
        }
        if (this.useLocal){
            this.emitter.add(this.particles)
        }else{
            this.scene.add(this.particles)
        }
    }

    resetParticle(id) {

        const tau = 6.28
        
        // position
        this.startPosition.x = this.bbox.min.x + Math.random()* (this.bbox.max.x - this.bbox.min.x)
        this.startPosition.y = this.bbox.min.y + Math.random()* (this.bbox.max.y - this.bbox.min.y)
        this.startPosition.z = this.bbox.min.z + Math.random()* (this.bbox.max.z - this.bbox.min.z)

        if (!this.useLocal){
            this.startPosition = this.emitter.localToWorld(this.startPosition);
        }
        
        this.positions[id].set(this.startPosition.x, this.startPosition.y, this.startPosition.z )


        // rotation
        this.rotations[id].x = Math.random() * tau
        this.rotations[id].y = Math.random() * tau
        this.rotations[id].z = Math.random() * tau

        // scale
        const varSX = this.particleScaleVariation.x * (Math.random() - .5);
        const varSY = this.particleScaleVariation.y * (Math.random() - .5);
        const varSZ = this.particleScaleVariation.z * (Math.random() - .5);
        this.scales[id].copy(this.particleScale).add(new THREE.Vector3(varSX, varSY, varSZ))

        this.times[id] = 0 // total time counter
        this.lifes[id] = this.particleLife + this.particleLifeVariation * (Math.random() - .5)
        this.delays[id] = Math.random() * this.particleMaxDelay

        if (this.followNormal){
            const varTSpeed = this.particleTransVariation * (Math.random() - .5);
            this.transSpeeds[id] = this.particleTransSpeed + varTSpeed;
        } else {
            const varTSpeedX = this.particleTransVariation.x * (Math.random() - .5);
            const varTSpeedY = this.particleTransVariation.y * (Math.random() - .5);
            const varTSpeedZ = this.particleTransVariation.z * (Math.random() - .5);
            this.transSpeeds[id].copy(this.particleTransSpeed).add(new THREE.Vector3(varTSpeedX, varTSpeedY, varTSpeedZ))
        }

        const varRSpeedX = this.particleRotVariation.x * (Math.random() - .5);
        const varRSpeedY = this.particleRotVariation.y * (Math.random() - .5);
        const varRSpeedZ = this.particleRotVariation.z * (Math.random() - .5);
        this.rotSpeeds[id].copy(this.particleRotSpeed).add(new THREE.Vector3(varRSpeedX, varRSpeedY, varRSpeedZ))    
        
        const varSSpeedX = this.particleScaleSpeedVariation.x * (Math.random() - .5);
        const varSSpeedY = this.particleScaleSpeedVariation.y * (Math.random() - .5);
        const varSSpeedZ = this.particleScaleSpeedVariation.z * (Math.random() - .5);
        this.scaleSpeeds[id].copy(this.particleScaleSpeed).add(new THREE.Vector3(varSSpeedX, varSSpeedY, varSSpeedZ))     
    }

    updateParticles(dt) {

        // no more particles alive
        if (this.aliveParticles === 0){
            return;
        }

        for (let i = 0; i < this.count; i++) {
            
            // particle has died
            if (this.isAlive[i] === 0){ 
                continue
            }

            // particle waiting to be launched
            if (this.times[i] < this.delays[i]) { 
                this.times[i] += dt
                continue
            }

            // particle wait ended, launch it
            if (this.isActive[i] === 0 && this.times[i] > this.delays[i] ) { 
                this.resetParticle(i)
                this.isActive[i] = 1
                continue
            }

            // particle has finished
            if (this.times[i] > this.delays[i] + this.lifes[i]) { 
                
                this.hideParticle(i)
                
                if (this.oneShot){
                    this.killParticle(i)
                }
                continue
            }

            // particle has reached minimum size
            if (this.scales[i].x < 0 || this.scales[i].y < 0 || this.scales[i].z < 0){
                this.hideParticle(i)
                if (this.oneShot){
                   this.killParticle(i)
                }
                continue
            }


            this.dummy.scale.copy(this.scales[i])
            this.dummy.scale.x += (this.dummy.scale.x > 0) ? dt * this.scaleSpeeds[i].x : 0
            this.dummy.scale.y += (this.dummy.scale.y > 0) ? dt * this.scaleSpeeds[i].y : 0
            this.dummy.scale.z += (this.dummy.scale.z > 0) ? dt * this.scaleSpeeds[i].z : 0

            this.dummy.position.copy(this.positions[i])
            
            if (this.followNormal){
                if (!this.useLocal){
                    this.emitter.getWorldDirection(this.normalDirection)
                }
                this.normalDirection.add(this.normalVariation)
                this.dummy.position.addScaledVector(this.normalDirection, dt * this.transSpeeds[i])
            } else {
                this.dummy.position.x += dt * this.transSpeeds[i].x
                this.dummy.position.y += dt * this.transSpeeds[i].y
                this.dummy.position.z += dt * this.transSpeeds[i].z
            }
            
            this.dummy.rotation.x = this.rotations[i].x + dt * this.rotSpeeds[i].x
            this.dummy.rotation.y = this.rotations[i].y + dt * this.rotSpeeds[i].y
            this.dummy.rotation.z = this.rotations[i].z + dt * this.rotSpeeds[i].z
            
            this.dummy.updateMatrix();
            this.particles.setMatrixAt(i, this.dummy.matrix);
            
            this.positions[i].copy(this.dummy.position)
            this.rotations[i].copy(this.dummy.rotation)
            this.scales[i].copy(this.dummy.scale);
            this.times[i] += dt
        }
        this.particles.instanceMatrix.needsUpdate = true

    }

    hideParticle(id){
        this.isActive[id] = 0
        this.times[id] = 0
        this.dummy.scale.set(0,0,0)
        this.dummy.updateMatrix();
        this.particles.setMatrixAt(id, this.dummy.matrix);
        this.scales[id].copy(this.dummy.scale);
    }

    killParticle(id){
        this.isAlive[id] = 0
        this.aliveParticles--;
        if (this.aliveParticles <= 0){
            //
        }
    }

    restartAll(){
        this.aliveParticles = this.count
        for (let i = 0; i < this.count; i++) {
            this.isActive[i] = 0
            this.isAlive[i] = 1
            this.resetParticle(i)
        }
    }

}